import { useEffect } from 'react';

export interface EdgeSwipe {
  startX: number;
  endX: number;
  dy: number;   // absolute vertical travel
  width: number; // viewport width
}

/**
 * A back-gesture: a drag that STARTS within 24px of the left edge, travels
 * rightward by at least max(80px, 25% of viewport width), and is more
 * horizontal than vertical (|dy| < travel/2). Pure so it's unit-tested without
 * touch events.
 */
export function classifyEdgeSwipe({ startX, endX, dy, width }: EdgeSwipe): boolean {
  if (startX > 24) return false;
  const travel = endX - startX;
  if (travel <= 0) return false;
  const threshold = Math.max(80, width * 0.25);
  if (travel < threshold) return false;
  return Math.abs(dy) < travel / 2;
}

/** Attach left-edge swipe-to-back. Disabled when `enabled` is false (no layer open). */
export function useEdgeSwipe(onBack: () => void, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let startX = 0, startY = 0, tracking = false;
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      startX = t.clientX; startY = t.clientY;
      tracking = startX <= 24;
    };
    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      if (classifyEdgeSwipe({ startX, endX: t.clientX, dy: Math.abs(t.clientY - startY), width: window.innerWidth })) {
        onBack();
      }
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [onBack, enabled]);
}
